import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Helmet } from 'react-helmet';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const AreaCalculator = () => {
  const [shape, setShape] = useState('rectangle');
  const [dimensions, setDimensions] = useState({
    length: '',
    width: '',
    radius: '',
    base: '',
    height: ''
  });
  const [area, setArea] = useState(null);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setDimensions(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const calculateArea = () => {
    let calculatedArea;

    switch (shape) {
      case 'rectangle':
        if (!dimensions.length || !dimensions.width) {
          setError('Please enter both length and width');
          return;
        }
        calculatedArea = parseFloat(dimensions.length) * parseFloat(dimensions.width);
        break;
      case 'circle':
        if (!dimensions.radius) {
          setError('Please enter the radius');
          return;
        }
        calculatedArea = Math.PI * Math.pow(parseFloat(dimensions.radius), 2);
        break;
      case 'triangle':
        if (!dimensions.base || !dimensions.height) {
          setError('Please enter both base and height');
          return;
        }
        calculatedArea = 0.5 * parseFloat(dimensions.base) * parseFloat(dimensions.height);
        break;
      default:
        setError('Please select a valid shape');
        return;
    }

    setArea(calculatedArea.toFixed(2));
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1.5 }}
      className="min-h-screen bg-gradient-to-r from-purple-100 to-pink-100"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Helmet>
          <title>Area Calculator</title>
          <meta name="description" content="Calculate the area of common shapes like rectangles, circles, and triangles." />
          <meta name="keywords" content="area calculator, calculate area, geometry calculator" />
        </Helmet>

        <div className="relative overflow-hidden bg-white rounded-lg shadow-2xl p-8 mb-8">
          <div className="calculator-header flex items-center mb-8">
            <div className="header-icon text-4xl text-pink-600 mr-4">📐</div>
            <h1 className="text-3xl font-bold text-gray-800">Area Calculator</h1>
          </div>

          <div className="text-gray-600 mb-8">
            <p>Calculate the area of common shapes like rectangles, circles, and triangles.</p>
          </div>

          <div className="grid grid-cols-1 gap-6 mb-6">
            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Select Shape:</label>
              <select
                value={shape}
                onChange={(e) => setShape(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
              >
                <option value="rectangle">Rectangle</option>
                <option value="circle">Circle</option>
                <option value="triangle">Triangle</option>
              </select>
            </div>

            {shape === 'rectangle' && (
              <>
                <div className="input-group">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Length:</label>
                  <input
                    type="text"
                    name="length"
                    value={dimensions.length}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                    placeholder="Enter length"
                  />
                </div>
                <div className="input-group">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Width:</label>
                  <input
                    type="text"
                    name="width"
                    value={dimensions.width}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                    placeholder="Enter width"
                  />
                </div>
              </>
            )}

            {shape === 'circle' && (
              <div className="input-group">
                <label className="block text-sm font-medium text-gray-700 mb-2">Radius:</label>
                <input
                  type="text"
                  name="radius"
                  value={dimensions.radius}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                  placeholder="Enter radius"
                />
              </div>
            )}

            {shape === 'triangle' && (
              <>
                <div className="input-group">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Base:</label>
                  <input
                    type="text"
                    name="base"
                    value={dimensions.base}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                    placeholder="Enter base"
                  />
                </div>
                <div className="input-group">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Height:</label>
                  <input
                    type="text"
                    name="height"
                    value={dimensions.height}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                    placeholder="Enter height"
                  />
                </div>
              </>
            )}
          </div>

          <button
            onClick={calculateArea}
            className="w-full py-3 px-4 bg-pink-600 text-white font-medium rounded-lg hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2 transition-colors duration-200"
          >
            Calculate Area
          </button>

          {error && (
            <div className="mt-4 p-4 text-red-700 bg-red-100 rounded-lg">
              {error}
            </div>
          )}

          {area && (
            <div className="mt-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">Results</h2>
              
              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-700 mb-4">Area</h3>
                <p className="text-lg">{area} square units</p>
              </div>
            </div>
          )}
        </div>

        {/* How to Use Section */}
        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">How to Use</h2>
          <div className="text-gray-600">
            <p>Follow these steps to use the Area Calculator:</p>
            <ol className="list-decimal pl-6 mt-4">
              <li><strong>Select Shape:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Choose the shape you want to calculate the area for (rectangle, circle, or triangle).</li>
                </ul>
              </li>
              <li><strong>Enter Dimensions:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Input the required dimensions for the selected shape.</li>
                </ul>
              </li>
              <li><strong>Click Calculate:</strong> The calculator will determine:
                <ul className="list-disc pl-6 mt-2">
                  <li>The area of the selected shape.</li>
                </ul>
              </li>
              <li><strong>Interpret Results:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Review the calculated area.</li>
                </ul>
              </li>
            </ol>
            <div className="mt-4 p-4 bg-blue-100 rounded-lg">
              <p><strong>💡 Pro Tips:</strong></p>
              <ul className="list-disc pl-6 mt-2">
                <li>Ensure all dimensions are in the same units (e.g., all in meters or all in inches).</li>
                <li>Use this calculator for geometry problems, construction projects, or any scenario involving area calculations.</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Example Calculation Section */}
        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Example Calculation</h2>
          <div className="text-gray-600">
            <p>Here's an example calculation for a rectangle:</p>
            
            <div className="bg-white p-6 rounded-lg shadow-md mt-4">
              <h4 className="mb-4">Sample Data:</h4>
              <ul className="list-disc pl-6 mb-4">
                <li>Shape: Rectangle</li>
                <li>Length: 10 units</li>
                <li>Width: 5 units</li>
              </ul>

              <h4 className="mb-4">Calculation Method:</h4>
              <ol className="list-decimal pl-6 mb-4">
                <li>Multiply the length by the width
                  <ul className="list-disc pl-6">
                    <li>Area = 10 * 5 = 50 square units</li>
                  </ul>
                </li>
              </ol>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">FAQ</h2>
          <div className="text-gray-600">
            <p>Here are some frequently asked questions about the Area Calculator:</p>
            <div className="mt-4 space-y-4">
              <div className="p-4 bg-gray-50 rounded-lg shadow-inner">
                <strong>Q: What shapes can I calculate the area for?</strong>
                <p>You can calculate the area for rectangles, circles, and triangles.</p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg shadow-inner">
                <strong>Q: How do I use the calculator?</strong>
                <p>Select the shape and enter the required dimensions, then click "Calculate Area" to get the result.</p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg shadow-inner">
                <strong>Q: Why is the area important?</strong>
                <p>The area is important for various applications, including geometry, construction, and design. It helps in understanding the size of a two-dimensional space.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default AreaCalculator;